import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Upload, Link2, ChevronDown, ChevronRight, FileSpreadsheet, Search, CheckSquare, Plus, Building2, Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { AccontoSelector } from '@/components/AccontoSelector';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ImportRapportiniPage() {
  const [rapportini, setRapportini] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [assignCantiere, setAssignCantiere] = useState('');
  const [assignAcconto, setAssignAcconto] = useState('');
  const [linking, setLinking] = useState(false);
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [search, setSearch] = useState('');
  // Create cliente dialog
  const [clienteDialogOpen, setClienteDialogOpen] = useState(false);
  const [creatingCliente, setCreatingCliente] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [pivaInput, setPivaInput] = useState('');
  const [clienteForm, setClienteForm] = useState({
    ragione_sociale: '', partita_iva: '', codice_fiscale: '', indirizzo: '',
    citta: '', cap: '', provincia: '', email: '', pec: '', telefono: '', codice_sdi: '', note: ''
  });
  const [cantiereForm, setCantiereForm] = useState({ name: '', address: '', description: '' });
  const [creaCantiereInline, setCreaCantiereInline] = useState(false);
  // Delete state
  const [deleting, setDeleting] = useState(false);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [deleteClientConfirm, setDeleteClientConfirm] = useState(null); // ragione_sociale to delete

  const [expandedCommesse, setExpandedCommesse] = useState(new Set());

  const toggleCommessa = (key) => {
    setExpandedCommesse(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Helpers to avoid nested ternaries in JSX
  const PIVA_SOURCE_LABELS = { openapi: 'Registro Imprese', VIES: 'VIES' };
  const getPivaSourceLabel = (source) => PIVA_SOURCE_LABELS[source] || 'archivio';

  const renderOperaiTecniciCell = (text) => {
    if (!text) return '-';
    const isSquadra = text.toLowerCase().includes('squadra');
    const className = isSquadra ? 'text-slate-500' : 'font-medium text-slate-700';
    return <span className={className}>{text}</span>;
  };

  const toggleSelectCommessa = (commessaRaps) => {
    const ids = commessaRaps.map(r => r.id);
    const allSel = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => allSel ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const fetchData = useCallback(async () => {
    try {
      const [impRes, cantRes] = await Promise.all([
        axios.get(`${API}/rapportini/imported`, { withCredentials: true }),
        axios.get(`${API}/cantieri`, { withCredentials: true })
      ]);
      setRapportini(impRes.data.rapportini || []);
      setCantieri(cantRes.data);
    } catch (err) {
      console.error('Errore caricamento rapportini importati:', err);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/rapportini/import-xls`, fd, { withCredentials: true });
      toast.success(`Importati ${res.data.imported} rapportini`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleLinkSelected = async () => {
    if (!assignCantiere || selected.size === 0) { toast.error('Seleziona rapportini e cantiere'); return; }
    if (!assignAcconto) { toast.error('Indica il riferimento: Acconto n° oppure Extra'); return; }
    setLinking(true);
    try {
      const ids = Array.from(selected);
      await axios.put(`${API}/rapportini/bulk-assign`, { rapportini_ids: ids, cantiere_id: assignCantiere, acconto_riferimento: assignAcconto }, { withCredentials: true });
      toast.success(`${ids.length} rapportini collegati`);
      setSelected(new Set());
      setAssignCantiere('');
      setAssignAcconto('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setLinking(false); }
  };

  // === DELETE FUNCTIONS ===
  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const ids = Array.from(selected);
      const res = await axios.delete(`${API}/rapportini/bulk-delete`, { data: { rapportini_ids: ids }, withCredentials: true });
      toast.success(`${res.data.deleted} rapportini eliminati`);
      setSelected(new Set());
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore eliminazione'); }
    finally { setDeleting(false); }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/rapportini/imported/all`, { withCredentials: true });
      toast.success(res.data.message);
      setSelected(new Set());
      setDeleteAllConfirm(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setDeleting(false); }
  };

  const handleDeleteClient = async (ragioneSociale) => {
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/rapportini/imported/client/${encodeURIComponent(ragioneSociale)}`, { withCredentials: true });
      toast.success(res.data.message);
      setSelected(new Set());
      setDeleteClientConfirm(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setDeleting(false); }
  };

  const toggleClient = (client) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      next.has(client) ? next.delete(client) : next.add(client);
      return next;
    });
  };

  const toggleSelectAll = (clientRaps) => {
    const ids = clientRaps.map(r => r.id);
    const allSelected = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const head = String(d).slice(0, 10).replace(/\//g, '-');
    const parts = head.split('-');
    return parts.length === 3 && parts.every(Boolean)
      ? `${parts[2]}-${parts[1]}-${parts[0]}`
      : String(d);
  };

  const handleLookupPiva = async () => {
    const cleaned = pivaInput.replace(/[^0-9]/g, '');
    if (!cleaned || cleaned.length < 11) { toast.error('P.IVA non valida (11 cifre)'); return; }
    setLookupLoading(true);
    try {
      const res = await axios.get(`${API}/lookup-piva/${cleaned}`, { withCredentials: true });
      if (res.data.valid && res.data.ragione_sociale) {
        setClienteForm(prev => ({
          ...prev,
          ragione_sociale: res.data.ragione_sociale || '',
          partita_iva: res.data.partita_iva_completa || `IT${res.data.partita_iva}`,
          indirizzo: res.data.indirizzo || '',
          citta: res.data.citta || '',
          cap: res.data.cap || '',
          provincia: res.data.provincia || '',
          codice_sdi: res.data.sdi_code || prev.codice_sdi || ''
        }));
        const sourceLabel = getPivaSourceLabel(res.data.source);
        toast.success(`Trovata: ${res.data.ragione_sociale} (${sourceLabel})`);
      } else {
        setClienteForm(prev => ({ ...prev, partita_iva: `IT${cleaned}` }));
        toast.info('P.IVA valida, compila i dati manualmente');
      }
    } catch { toast.error('Errore nella ricerca P.IVA'); }
    finally { setLookupLoading(false); }
  };

  const openClienteDialog = (ragioneSociale) => {
    setClienteForm({
      ragione_sociale: ragioneSociale || '', partita_iva: '', codice_fiscale: '', indirizzo: '',
      citta: '', cap: '', provincia: '', email: '', pec: '', telefono: '', codice_sdi: '', note: ''
    });
    setCantiereForm({ name: '', address: '', description: '' });
    setCreaCantiereInline(false);
    setPivaInput('');
    setClienteDialogOpen(true);
  };

  const handleCreateCliente = async () => {
    if (!clienteForm.ragione_sociale.trim()) { toast.error('Ragione sociale obbligatoria'); return; }
    setCreatingCliente(true);
    try {
      const clienteRes = await axios.post(`${API}/clienti`, clienteForm, { withCredentials: true });
      const newClienteId = clienteRes.data.id;
      toast.success(`Cliente "${clienteForm.ragione_sociale}" creato`);
      if (creaCantiereInline && cantiereForm.name.trim()) {
        await axios.post(`${API}/cantieri`, { ...cantiereForm, cliente_id: newClienteId }, { withCredentials: true });
        toast.success(`Cantiere "${cantiereForm.name}" creato e collegato`);
      }
      setClienteDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore creazione cliente'); }
    finally { setCreatingCliente(false); }
  };

  // Group by client
  const grouped = {};
  rapportini.forEach(r => {
    const key = r.ragione_sociale || r.cantiere_id || '?';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  });

  const filteredClients = Object.entries(grouped).filter(([client]) =>
    !search || client.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => b[1].length - a[1].length);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="import-rapportini-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Importa Rapportini</h1>
          <p className="text-slate-500 mt-1">Carica XLS, seleziona e collega ai cantieri</p>
        </div>
        <div className="flex gap-2">
          {rapportini.length > 0 && (
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={() => setDeleteAllConfirm(true)} data-testid="delete-all-btn">
              <Trash2 className="h-4 w-4 mr-2" />Elimina Tutti
            </Button>
          )}
          <Button variant="outline" onClick={() => openClienteDialog('')} data-testid="create-cliente-btn">
            <Plus className="h-4 w-4 mr-2" />Nuovo Cliente
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".xls,.xlsx" className="hidden" onChange={handleUpload} data-testid="upload-xls-input" />
            <div className="inline-flex items-center justify-center h-10 px-6 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors">
              <Upload className="h-4 w-4 mr-2" />{uploading ? 'Importazione...' : 'Carica File XLS'}
            </div>
          </label>
        </div>
      </div>

      {rapportini.length === 0 ? (
        <div className="text-center py-16">
          <FileSpreadsheet className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600">Nessun rapportino importato</h3>
          <p className="text-sm text-slate-400 mt-1">Carica un file XLS per iniziare</p>
        </div>
      ) : (
        <>
          {/* Action bar when items selected */}
          {selected.size > 0 && (
            <Card className="border-blue-300 bg-blue-50 sticky top-0 z-10">
              <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                <Badge className="bg-blue-600 text-white text-sm px-3 py-1">
                  <CheckSquare className="h-4 w-4 mr-1" />{selected.size} selezionati
                </Badge>
                <Select value={assignCantiere} onValueChange={setAssignCantiere}>
                  <SelectTrigger className="w-[280px] bg-white" data-testid="assign-cantiere-select">
                    <SelectValue placeholder="Scegli cantiere..." />
                  </SelectTrigger>
                  <SelectContent>
                    {cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <AccontoSelector compact value={assignAcconto} onChange={setAssignAcconto} dataTestId="assign-acconto-select" />
                <Button disabled={!assignCantiere || !assignAcconto || linking} onClick={handleLinkSelected} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="link-selected-btn">
                  <Link2 className="h-4 w-4 mr-2" />{linking ? 'Collegamento...' : 'Collega'}
                </Button>
                <Button variant="destructive" disabled={deleting} onClick={handleDeleteSelected} data-testid="delete-selected-btn">
                  <Trash2 className="h-4 w-4 mr-2" />{deleting ? 'Eliminazione...' : 'Elimina Selezionati'}
                </Button>
                <Button variant="ghost" onClick={() => setSelected(new Set())} className="text-slate-500">Deseleziona</Button>
              </CardContent>
            </Card>
          )}

          {/* Search + Stats */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Cerca cliente..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="search-client" />
            </div>
            <Badge variant="outline" className="text-sm">{rapportini.length} rapportini</Badge>
            <Badge variant="outline" className="text-sm">{Object.keys(grouped).length} clienti</Badge>
          </div>

          {/* Client accordion */}
          <div className="space-y-2">
            {filteredClients.map(([client, raps]) => {
              const isExpanded = expandedClients.has(client);
              const clientSelected = raps.filter(r => selected.has(r.id)).length;
              const allSelected = clientSelected === raps.length;
              const commesse = [...new Set(raps.map(r => r.commessa).filter(Boolean))];
              const dateRange = raps.length > 0 ? `${formatDate(raps[raps.length-1]?.date)} - ${formatDate(raps[0]?.date)}` : '';

              return (
                <Card key={client} className={`border-slate-200 ${clientSelected > 0 ? 'ring-2 ring-blue-300' : ''}`}>
                  <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => toggleClient(client)}>
                    <Checkbox checked={allSelected} onCheckedChange={() => { toggleSelectAll(raps); if (!isExpanded) toggleClient(client); }} onClick={e => e.stopPropagation()} />
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{client}</span>
                        {commesse.length > 0 && (
                          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                            Commessa: {commesse.join(', ')}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">{dateRange}</span>
                    </div>
                    <Badge variant="outline">{raps.length} rapp.</Badge>
                    {clientSelected > 0 && <Badge className="bg-blue-100 text-blue-700">{clientSelected} sel.</Badge>}
                    <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-600 h-8 w-8 p-0" title="Elimina tutti i rapportini di questo cliente" onClick={e => { e.stopPropagation(); setDeleteClientConfirm(client); }} data-testid={`delete-client-${client}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-slate-400 hover:text-emerald-600 h-8 w-8 p-0" title="Crea cliente" onClick={e => { e.stopPropagation(); openClienteDialog(client); }}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {isExpanded && (() => {
                    // Group raps by commessa
                    const byCommessa = {};
                    raps.forEach(r => {
                      const cKey = r.commessa || 'senza_commessa';
                      if (!byCommessa[cKey]) byCommessa[cKey] = [];
                      byCommessa[cKey].push(r);
                    });
                    const commessaEntries = Object.entries(byCommessa).sort((a, b) => {
                      if (a[0] === 'senza_commessa') return 1;
                      if (b[0] === 'senza_commessa') return -1;
                      return Number(a[0]) - Number(b[0]);
                    });

                    return (
                      <div className="border-t border-slate-100">
                        {commessaEntries.map(([commKey, commRaps]) => {
                          const commUniqueKey = `${client}__${commKey}`;
                          const isCommExpanded = expandedCommesse.has(commUniqueKey);
                          const commSelected = commRaps.filter(r => selected.has(r.id)).length;
                          const allCommSel = commSelected === commRaps.length;
                          const commDateRange = commRaps.length > 0 ? `${formatDate(commRaps[commRaps.length-1]?.date)} - ${formatDate(commRaps[0]?.date)}` : '';

                          return (
                            <div key={commKey} className="border-b border-slate-50 last:border-b-0">
                              <div className="flex items-center gap-3 px-6 py-3 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => toggleCommessa(commUniqueKey)}>
                                <Checkbox checked={allCommSel} onCheckedChange={() => toggleSelectCommessa(commRaps)} onClick={e => e.stopPropagation()} />
                                {isCommExpanded ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                                <Badge variant="outline" className={`text-xs ${commKey === 'senza_commessa' ? 'bg-slate-50 text-slate-500 border-slate-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                  {commKey === 'senza_commessa' ? 'Senza commessa' : `Commessa #${commKey}`}
                                </Badge>
                                <span className="text-xs text-slate-400">{commDateRange}</span>
                                <span className="text-xs text-slate-500 ml-auto">{commRaps.length} rapp.</span>
                                {commSelected > 0 && <Badge className="bg-blue-100 text-blue-700 text-xs">{commSelected} sel.</Badge>}
                              </div>
                              {isCommExpanded && (
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="w-10 pl-10"></TableHead>
                                      <TableHead className="w-20">Data</TableHead>
                                      <TableHead>Descrizione</TableHead>
                                      <TableHead className="w-40">Operai/Tecnici</TableHead>
                                      <TableHead className="w-28">Operatore</TableHead>
                                      <TableHead className="w-16">Sq.</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {commRaps.map(r => (
                                      <TableRow key={r.id} className={selected.has(r.id) ? 'bg-blue-50' : ''}>
                                        <TableCell className="pl-10"><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleSelect(r.id)} /></TableCell>
                                        <TableCell className="text-xs whitespace-nowrap">{formatDate(r.date)}</TableCell>
                                        <TableCell className="text-sm">{r.description || '-'}</TableCell>
                                        <TableCell className="text-xs">
                                          {renderOperaiTecniciCell(r.operai_tecnici)}
                                        </TableCell>
                                        <TableCell className="text-xs text-slate-500">{r.worker_name}</TableCell>
                                        <TableCell><Badge variant="outline" className="text-[10px]">{r.num_workers || 1}p</Badge></TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Confirm Delete All */}
      <AlertDialog open={deleteAllConfirm} onOpenChange={setDeleteAllConfirm}>
        <AlertDialogContent data-testid="delete-all-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" />Elimina tutti i rapportini importati</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati <strong>{rapportini.length}</strong> rapportini importati non ancora assegnati. Questa azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAll} className="bg-red-600 hover:bg-red-700 text-white" disabled={deleting} data-testid="confirm-delete-all-btn">
              {deleting ? 'Eliminazione...' : 'Elimina Tutti'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Delete Client */}
      <AlertDialog open={!!deleteClientConfirm} onOpenChange={() => setDeleteClientConfirm(null)}>
        <AlertDialogContent data-testid="delete-client-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" />Elimina rapportini cliente</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati tutti i rapportini importati di <strong>{deleteClientConfirm}</strong> ({grouped[deleteClientConfirm]?.length || 0} rapportini). Questa azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteClient(deleteClientConfirm)} className="bg-red-600 hover:bg-red-700 text-white" disabled={deleting} data-testid="confirm-delete-client-btn">
              {deleting ? 'Eliminazione...' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Crea Cliente */}
      <Dialog open={clienteDialogOpen} onOpenChange={setClienteDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-cliente-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" />Nuovo Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ricerca per P.IVA</Label>
              <div className="flex gap-2 mt-1">
                <Input placeholder="es. 01234567890" value={pivaInput} onChange={e => setPivaInput(e.target.value)} data-testid="piva-lookup-input" />
                <Button variant="outline" onClick={handleLookupPiva} disabled={lookupLoading} data-testid="piva-lookup-btn">
                  {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Ragione Sociale *</Label>
                <Input value={clienteForm.ragione_sociale} onChange={e => setClienteForm(f => ({ ...f, ragione_sociale: e.target.value }))} data-testid="cliente-ragione-sociale" />
              </div>
              <div><Label>Partita IVA</Label><Input value={clienteForm.partita_iva} onChange={e => setClienteForm(f => ({ ...f, partita_iva: e.target.value }))} /></div>
              <div><Label>Codice Fiscale</Label><Input value={clienteForm.codice_fiscale} onChange={e => setClienteForm(f => ({ ...f, codice_fiscale: e.target.value }))} /></div>
              <div className="col-span-2"><Label>Indirizzo</Label><Input value={clienteForm.indirizzo} onChange={e => setClienteForm(f => ({ ...f, indirizzo: e.target.value }))} /></div>
              <div><Label>Citta</Label><Input value={clienteForm.citta} onChange={e => setClienteForm(f => ({ ...f, citta: e.target.value }))} /></div>
              <div><Label>CAP</Label><Input value={clienteForm.cap} onChange={e => setClienteForm(f => ({ ...f, cap: e.target.value }))} /></div>
              <div><Label>Provincia</Label><Input value={clienteForm.provincia} onChange={e => setClienteForm(f => ({ ...f, provincia: e.target.value }))} /></div>
              <div><Label>Telefono</Label><Input value={clienteForm.telefono} onChange={e => setClienteForm(f => ({ ...f, telefono: e.target.value }))} /></div>
              <div><Label>Email</Label><Input value={clienteForm.email} onChange={e => setClienteForm(f => ({ ...f, email: e.target.value }))} /></div>
              <div><Label>PEC</Label><Input value={clienteForm.pec} onChange={e => setClienteForm(f => ({ ...f, pec: e.target.value }))} /></div>
              <div><Label>Codice SDI</Label><Input value={clienteForm.codice_sdi} onChange={e => setClienteForm(f => ({ ...f, codice_sdi: e.target.value }))} /></div>
            </div>
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <Checkbox checked={creaCantiereInline} onCheckedChange={setCreaCantiereInline} data-testid="create-cantiere-inline-check" />
                <Label className="cursor-pointer" onClick={() => setCreaCantiereInline(!creaCantiereInline)}>Crea anche un cantiere per questo cliente</Label>
              </div>
              {creaCantiereInline && (
                <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-emerald-300">
                  <div className="col-span-2"><Label>Nome Cantiere *</Label><Input value={cantiereForm.name} onChange={e => setCantiereForm(f => ({ ...f, name: e.target.value }))} placeholder="es. Ristrutturazione Via Roma 15" data-testid="cantiere-name-input" /></div>
                  <div className="col-span-2"><Label>Indirizzo Cantiere</Label><Input value={cantiereForm.address} onChange={e => setCantiereForm(f => ({ ...f, address: e.target.value }))} /></div>
                  <div className="col-span-2"><Label>Descrizione</Label><Input value={cantiereForm.description} onChange={e => setCantiereForm(f => ({ ...f, description: e.target.value }))} /></div>
                </div>
              )}
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleCreateCliente} disabled={creatingCliente} data-testid="save-cliente-btn">
              {creatingCliente ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {creaCantiereInline ? 'Crea Cliente e Cantiere' : 'Crea Cliente'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
